import { hasProperty } from "@suse-o11y/has-property";
import { isFunction } from "@suse-o11y/is-function";
import { Slot, type Slottable } from "@suse-o11y/slot";
import React from "react";

import { ZIndexBoundaryProvider, useClosestZIndex } from "./_context";

// TODO OBSFE-21 Utilize @suse-o11y/setter
export type ZIndexSetter = number | ((closestZIndex: undefined | number) => number);

export interface ZIndexBoundaryForwarderProps {
	children: (zIndex: undefined | number) => React.ReactNode;
}

export interface ZIndexBoundaryRendererProps {
	zIndex: ZIndexSetter;
	children: (zIndex: number) => React.ReactNode;
}

function isZIndexBoundaryRendererProps(props: ZIndexBoundaryProps): props is ZIndexBoundaryRendererProps {
	return hasProperty(props, "zIndex") && isFunction(props.children);
}

export interface ZIndexBoundaryComponentProps extends Slottable, React.HTMLAttributes<HTMLDivElement> {
	zIndex: ZIndexSetter;
}

function isZIndexBoundaryComponentProps(props: ZIndexBoundaryProps): props is ZIndexBoundaryComponentProps {
	return !isFunction(props.children);
}

export type ZIndexBoundaryProps =
	| ZIndexBoundaryForwarderProps
	| ZIndexBoundaryRendererProps
	| ZIndexBoundaryComponentProps;

export const ZIndexBoundary = React.forwardRef(
	(props: ZIndexBoundaryProps, ref: React.ForwardedRef<HTMLDivElement>) => {
		const closestZIndex = useClosestZIndex();

		if (isZIndexBoundaryComponentProps(props)) {
			const { asChild, style, zIndex, ...rest } = props;
			// TODO OBSFE-21 Utilize @suse-o11y/setter
			const nextZIndex = isFunction(zIndex) ? zIndex(closestZIndex) : zIndex;

			const Component = asChild ? Slot : "div";

			return (
				<ZIndexBoundaryProvider value={nextZIndex}>
					<Component ref={ref} style={{ zIndex: nextZIndex, ...style }} {...rest} />
				</ZIndexBoundaryProvider>
			);
		}

		if (isZIndexBoundaryRendererProps(props)) {
			const { zIndex, children } = props;
			// TODO OBSFE-21 Utilize @suse-o11y/setter
			const nextZIndex = isFunction(zIndex) ? zIndex(closestZIndex) : zIndex;

			return <ZIndexBoundaryProvider value={nextZIndex}>{children(nextZIndex)}</ZIndexBoundaryProvider>;
		}

		return <>{props.children(closestZIndex)}</>;
	},
) as {
	(props: ZIndexBoundaryRendererProps): React.ReactElement;
	(props: ZIndexBoundaryForwarderProps): React.ReactElement;
	(props: ZIndexBoundaryComponentProps, ref: React.ForwardedRef<HTMLDivElement>): React.ReactElement;

	displayName?: string;
};

if (import.meta.env.DEV) {
	ZIndexBoundary.displayName = "ZIndexBoundary";
}
